// src/sagas/websocketSaga.js
import {
  take,
  call,
  put,
  fork,
  cancelled,
  cancel,
  takeEvery,
  delay,
} from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import {
  SAGA_CONNECT_WEBSOCKET_V2,
  SAGA_MESSAGE_WEBSOCKET_V2,
  CONNECT_WEBSOCKET_V2,
  DISCONNECT_WEBSOCKET_V2,
  MESSAGE_WEBSOCKET,
  ERROR_WEBSOCKET,
} from "../constants/actions";
import { WEBSOCKET_URL_V2 } from "../constants/api";

// WebSocket singleton management
let activeSocket = null;

function createSocketChannel(socket, web_chat_id) {
  return eventChannel((emit) => {
    socket.onopen = () => {
      console.log(`WebSocket connected for web_chat_id ${web_chat_id}`);
      emit({ type: CONNECT_WEBSOCKET_V2 });
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // console.log("WebSocket received:", data);

        // Generate consistent message ID if not provided
        const messageId = data.content?.id || `msg-${Date.now()}`;

        const baseMessage = {
          id: messageId,
          web_chat_id,
          from: "assistant",
          timestamp: new Date().toISOString(),
        };

        switch (data.type) {
          case "stream-start":
            emit({
              type: MESSAGE_WEBSOCKET,
              payload: {
                ...baseMessage,
                message: "",
                status: "streaming",
              },
            });
            break;

          case "stream-chunk":
            emit({
              type: MESSAGE_WEBSOCKET,
              payload: {
                ...baseMessage,
                message: data.content?.chunk || "",
                status: "streaming",
              },
            });
            break;

          case "stream-end":
            // Don't emit empty completion message
            if (!data.content?.chunk) break;
            emit({
              type: MESSAGE_WEBSOCKET,
              payload: {
                ...baseMessage,
                message: data.content?.chunk || "",
                status: "complete",
                finalTimestamp: new Date().toISOString(),
              },
            });
            break;

          default:
            console.log("Unhandled message type:", data);
            break;
        }
      } catch (error) {
        console.error("WebSocket message parsing error:", error);
        emit({ type: ERROR_WEBSOCKET, payload: "Failed to parse message" });
      }
    };

    socket.onclose = () => {
      console.log(`WebSocket disconnected for web_chat_id ${web_chat_id}`);
      emit({ type: DISCONNECT_WEBSOCKET_V2 });
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      emit({ type: ERROR_WEBSOCKET, payload: error.message });
    };

    return () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    };
  });
}

function* handleSocketConnection(action) {
  const { web_chat_id } = action.payload;

  try {
    // Close existing socket first
    if (activeSocket?.readyState === WebSocket.OPEN) {
      yield call([activeSocket, "close"]);
    }

    // Create new connection
    activeSocket = new WebSocket(WEBSOCKET_URL_V2(web_chat_id));
    const socketChannel = yield call(
      createSocketChannel,
      activeSocket,
      web_chat_id
    );

    // Wait for successful connection before proceeding
    let connected = false;
    while (!connected) {
      const event = yield take(socketChannel);
      yield put(event);
      if (event.type === CONNECT_WEBSOCKET_V2) {
        connected = true;
      }
    }

    // Now that we're connected, process remaining events
    while (true) {
      const event = yield take(socketChannel);
      yield put(event);
    }
  } catch (error) {
    console.error("WebSocket connection error:", error);
    yield put({ type: ERROR_WEBSOCKET, payload: error.message });
  }
}

function* handleSendMessage(action) {
  // Maximum number of retries
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 second

  for (let i = 0; i < MAX_RETRIES; i++) {
    if (!activeSocket) {
      console.error("No active socket connection");
      yield delay(RETRY_DELAY);
      continue;
    }

    if (activeSocket.readyState === WebSocket.CONNECTING) {
      console.log("Socket still connecting, waiting...");
      yield delay(RETRY_DELAY);
      continue;
    }

    if (activeSocket.readyState === WebSocket.OPEN) {
      try {
        const messagePayload = {
          ...action.payload,
          timestamp: action.payload.timestamp || new Date().toISOString(),
        };

        console.log("Sending message:", messagePayload);
        activeSocket.send(JSON.stringify(messagePayload));
        return; // Success, exit function
      } catch (error) {
        console.error("Message send error:", error);
        yield put({ type: ERROR_WEBSOCKET, payload: error.message });
        return;
      }
    } else {
      console.error("Socket in invalid state:", activeSocket.readyState);
      yield put({
        type: ERROR_WEBSOCKET,
        payload: `Socket in invalid state: ${activeSocket.readyState}`,
      });
      return;
    }
  }

  // If we get here, we've exhausted all retries
  yield put({
    type: ERROR_WEBSOCKET,
    payload: "Failed to send message after multiple retries",
  });
}

function* watchSocketEvents() {
  while (true) {
    // Wait for connect action
    const connectAction = yield take(SAGA_CONNECT_WEBSOCKET_V2);

    // Start connection process
    const connectionTask = yield fork(handleSocketConnection, connectAction);

    // Watch for messages while connected
    const messageWatcher = yield fork(function* () {
      yield takeEvery(SAGA_MESSAGE_WEBSOCKET_V2, handleSendMessage);
    });

    // Wait for disconnect
    yield take(DISCONNECT_WEBSOCKET_V2);

    // Cancel both tasks on disconnect
    yield cancel(connectionTask);
    yield cancel(messageWatcher);
  }
}

export default function* websocketSaga() {
  yield fork(watchSocketEvents);
}
